import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2'; 
import { db } from './Firebase';
import { collection, setDoc, doc, query, orderBy, limit, getDocs, getDoc } from 'firebase/firestore';

const initialFormData = {
  applicationId: '',
  gender: '',
  firstName: '',
  lastName: '',
  firstNameEng: '',
  lastNameEng: '',
  nickname: '',
  dob: '',
  idCardNumber: '',
  currentSchool: '',
  grade: '',
  schoolProvince: '',
  studentProvince: '',
  parentFirstName: '',
  parentLastName: '',
  email: '',
  contact1: '',
  lineId: '',
  tmscSource: '',
};

const formatDateInput = (value) => {
  let newValue = value.replace(/[^\d]/g, '');
  
  if (newValue.length > 8) {
    newValue = newValue.substring(0, 8);
  }
  
  if (newValue.length > 4) {
    newValue = newValue.substring(0, 2) + '-' + 
               newValue.substring(2, 4) + '-' + 
               newValue.substring(4);
  } else if (newValue.length > 2) {
    newValue = newValue.substring(0, 2) + '-' + 
               newValue.substring(2);
  }
  
  return newValue;
};

// 格式化电话号码的函数
const formatContactInput = (value) => {
  // 仅保留数字
  let digitsOnly = value.replace(/[^\d]/g, '');
  
  // 限制长度不超过10位
  if (digitsOnly.length > 10) {
    digitsOnly = digitsOnly.substring(0, 10);
  }
  
  // 如果数字少于3位，直接返回
  if (digitsOnly.length < 3) {
    return digitsOnly;
  }
  
  // 格式化为 XXX-XXXXXXX
  return digitsOnly.substring(0, 3) + '-' + digitsOnly.substring(3);
};

function StudentForm() {
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingApplicationId, setIsLoadingApplicationId] = useState(false);
  const [showSuccessImage, setShowSuccessImage] = useState(false);

  const fetchNextApplicationId = async () => {
    try {
      setIsLoadingApplicationId(true);
      
      const q = query(collection(db, "student-register"), orderBy("ID", "desc"), limit(1));
      const querySnapshot = await getDocs(q);
      
      let nextId = 1;
      if (!querySnapshot.empty) {
        const lastDoc = querySnapshot.docs[0].data();
        if (lastDoc.ID) {
          nextId = Number(lastDoc.ID) + 1;
        }
      }

      setFormData(prev => ({
        ...prev,
        applicationId: nextId.toString()
      }));

    } catch (error) {
      console.error('Error fetching application ID from Firestore:', error);
      Swal.fire({
        icon: 'error',
        title: 'เกิดข้อผิดพลาด',
        text: `ไม่สามารถดึงเลขใบสมัครได้: ${error.message}`
      });
    } finally {
      setIsLoadingApplicationId(false);
    }
  };

  useEffect(() => {
    fetchNextApplicationId();
  }, []); // Remove googleAppsScriptUrl dependency as we use Firestore now

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'idCardNumber') {
      const digitsOnly = value.replace(/[^\d]/g, '').substring(0, 13);
      setFormData(prev => ({ ...prev, [name]: digitsOnly }));
      return;
    }

    if (name === 'dob') {
      const formattedValue = formatDateInput(value);
      setFormData(prev => ({ ...prev, dob: formattedValue }));
      return;
    }
    
    // 格式化联系电话字段
    if (name === 'contact1') {
      const formattedValue = formatContactInput(value);
      setFormData(prev => ({ ...prev, [name]: formattedValue }));
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const resetForm = async () => {
    setFormData(initialFormData);
    await fetchNextApplicationId();
  };

  const validateForm = () => {
    const requiredFields = [
      'applicationId', 
      'firstName', 
      'lastName', 
      'firstNameEng',
      'lastNameEng',
      'gender', 
      'idCardNumber', 
      'nickname', 
      'currentSchool', 
      'grade', 
      'schoolProvince',
      'studentProvince',
      'contact1',
      'dob',
      'parentFirstName',
      'parentLastName',
      'tmscSource'
    ]; 
    const missingFields = [];

    requiredFields.forEach(field => {
      if (!formData[field]?.toString().trim()) {
        missingFields.push(field);
      }
    });

    if (missingFields.length > 0) {
      const fieldNames = {
        applicationId: 'เลขที่ใบสมัคร',
        firstName: 'ชื่อ',
        lastName: 'นามสกุล',
        firstNameEng: 'ชื่อ เป็นภาษาอังกฤษ',
        lastNameEng: 'นามสกุล เป็นภาษาอังกฤษ',
        gender: 'เพศ',
        idCardNumber: 'หมายเลขบัตรประชาชน',
        nickname: 'ชื่อเล่น',
        currentSchool: 'โรงเรียนที่เรียนอยู่ปัจจุบัน',
        grade: 'ระดับชั้น',
        schoolProvince: 'จังหวัด (ของสถานศึกษา)',
        studentProvince: 'จังหวัดที่ผู้สมัครพักอาศัย',
        contact1: 'เบอร์โทรติดต่อ',
        dob: 'วันเกิด',
        parentFirstName: 'ชื่อผู้ปกครอง',
        parentLastName: 'นามสกุลผู้ปกครอง',
        tmscSource: 'ทราบข้อมูลโครงการ TMSC จากช่องทางไหน'
      };
      const missingFieldNames = missingFields.map(field => fieldNames[field] || field);
      
      Swal.fire({
        icon: 'error',
        title: 'ข้อมูลไม่ครบถ้วน',
        text: `กรุณากรอกข้อมูลที่จำเป็น: ${missingFieldNames.join(', ')}`,
        confirmButtonColor: '#3085d6',
      });
      
      return false;
    }

    if (!/^\d{2}-\d{2}-\d{4}$/.test(formData.dob)) {
      Swal.fire({
        icon: 'error',
        title: 'รูปแบบวันเกิดไม่ถูกต้อง',
        text: 'กรุณากรอกในรูปแบบ DD-MM-YYYY (เช่น 01-01-2560)',
        confirmButtonColor: '#3085d6',
      });
      return false;
    }

    const [day, month, year] = formData.dob.split('-').map(Number);
    const date = new Date(year - 543, month - 1, day);
    
    if (
      isNaN(date.getTime()) || 
      date.getDate() !== day || 
      (date.getMonth() + 1) !== month ||
      date.getFullYear() !== (year - 543)
    ) {
      Swal.fire({
        icon: 'error',
        title: 'วันเกิดไม่ถูกต้อง',
        text: 'กรุณาตรวจสอบวันที่ให้ถูกต้อง',
        confirmButtonColor: '#3085d6',
      });
      return false;
    }

    if (year < 2500) {
      Swal.fire({
        icon: 'error',
        title: 'ปีที่กรอกไม่ถูกต้อง',
        text: 'ปีที่กรอกต้องเป็นพุทธศักราช (อย่างน้อย 2500 ขึ้นไป)',
        confirmButtonColor: '#3085d6',
      });
      return false;
    }

    if (!/^\d{13}$/.test(formData.idCardNumber)) {
      Swal.fire({
        icon: 'error',
        title: 'หมายเลขบัตรประชาชนไม่ถูกต้อง',
        text: 'หมายเลขบัตรประชาชนต้องเป็นตัวเลข 13 หลัก',
        confirmButtonColor: '#3085d6',
      });
      return false;
    }

    // 验证电话格式
    if (!/^[0-9-]{3,11}$/.test(formData.contact1)) {
      Swal.fire({
        icon: 'error',
        title: 'เบอร์โทรศัพท์ไม่ถูกต้อง',
        text: 'เบอร์โทรศัพท์ต้องอยู่ในรูปแบบ XXX-XXXXXXX',
        confirmButtonColor: '#3085d6',
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    Swal.fire({
      title: 'กำลังบันทึกข้อมูล',
      html: 'กรุณารอสักครู่...',
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      }
    });

    setIsSubmitting(true);

    try {
      const submissionData = { ...formData };

      const engNameLower = (submissionData.firstNameEng || '').toLowerCase();
      const engSurnameLower = (submissionData.lastNameEng || '').substring(0, 3).toLowerCase();
      const itemName = `${engNameLower}-${engSurnameLower}`;

      const docRef = doc(db, "temp", itemName);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        Swal.close();
        const result = await Swal.fire({
          title: 'ข้อมูลซ้ำซ้อน',
          text: `พบข้อมูลผู้สมัครรหัส "${itemName}" อยู่ในระบบแล้ว คุณต้องการเขียนทับข้อมูลเดิมหรือไม่?`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#d33',
          confirmButtonText: 'ใช่, เขียนทับเลย',
          cancelButtonText: 'ยกเลิก'
        });

        if (!result.isConfirmed) {
          setIsSubmitting(false);
          return;
        }

        // เปิดหน้าต่าง loading กลับมาอีกครั้งเพื่อทำการบันทึก
        Swal.fire({
          title: 'กำลังบันทึกข้อมูล',
          html: 'กรุณารอสักครู่...',
          allowOutsideClick: false,
          didOpen: () => {
            Swal.showLoading();
          }
        });
      }

      const firestoreData = {
        userdata: {
          'thai-name': submissionData.firstName || '',
          'thai-surname': submissionData.lastName || '',
          'eng-name': submissionData.firstNameEng || '',
          'eng-surname': submissionData.lastNameEng || '',
          'nickname': submissionData.nickname || '',
          'sex': submissionData.gender || '',
          'birthday': submissionData.dob || '',
          'idCardNumber': Number(submissionData.idCardNumber) || 0,
          'school': submissionData.currentSchool || '',
          'school-level': submissionData.grade || '',
          'school-province': submissionData.schoolProvince || '',
          'student-province': submissionData.studentProvince || '',
          'parent-name': submissionData.parentFirstName || '',
          'parent-surname': submissionData.parentLastName || '',
          'telephone': submissionData.contact1 || '',
          'line-id': submissionData.lineId || '',
          'email': submissionData.email || '',
          'tmsc-source': submissionData.tmscSource || ''
        }
      };

      await setDoc(docRef, firestoreData, { merge: true });
      
      Swal.close();
      Swal.fire({
        icon: 'success',
        title: 'บันทึกข้อมูลสำเร็จ!',
        text: 'ข้อมูลถูกบันทึกเรียบร้อย',
        confirmButtonColor: '#3085d6',
      }).then(() => {
        resetForm();
        setShowSuccessImage(true);
      });

    } catch (error) {
      console.error('Submission error:', error);
      Swal.fire({
        icon: 'error',
        title: 'บันทึกข้อมูลล้มเหลว',
        text: error.message || 'เกิดข้อผิดพลาดที่ไม่คาดคิด'
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <div className="student-form-container">
      <form onSubmit={handleSubmit} className="student-form">
        <header className="form-header" style={{ padding: 0, background: 'none' }}>
          <img 
            src={process.env.PUBLIC_URL + '/header.png'} 
            alt="ลงทะเบียนการแข่งขันจินตคณิตออนไลน์" 
            className="header-banner-image"
          />
        </header>
        
        <div className="form-section">
          <div className="form-section-header">
            <h2>ข้อมูลผู้เข้าแข่งขัน</h2>
          </div>


          <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="gender">เพศ <span className="required">*</span></label>
              <select
                id="gender"
                name="gender"
                value={formData.gender}
                onChange={handleChange}
                required
                style={{ width: '100%' }}
              >
                <option value="">-- เลือกเพศ --</option>
                <option value="ด.ช.">ด.ช.</option>
                <option value="ด.ญ.">ด.ญ.</option>

              </select>
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="nickname">ชื่อเล่น <span className="required">*</span></label>
              <input 
                type="text" 
                id="nickname" 
                name="nickname" 
                value={formData.nickname} 
                onChange={handleChange}
                style={{ width: '100%' }} 
                required
              />
            </div>
          </div>

          <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="firstName">ชื่อ เป็นภาษาไทย <span className="required">*</span></label>
              <input 
                type="text" 
                id="firstName" 
                name="firstName" 
                value={formData.firstName} 
                onChange={handleChange} 
                required 
              />
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="lastName">นามสกุล เป็นภาษาไทย <span className="required">*</span></label>
              <input 
                type="text" 
                id="lastName" 
                name="lastName" 
                value={formData.lastName} 
                onChange={handleChange} 
                required 
              />
            </div>
          </div>

          <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="firstNameEng">ชื่อ เป็นภาษาอังกฤษ <span className="required">*</span></label>
              <input 
                type="text" 
                id="firstNameEng" 
                name="firstNameEng" 
                value={formData.firstNameEng} 
                onChange={handleChange} 
                required 
              />
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="lastNameEng">นามสกุล เป็นภาษาอังกฤษ <span className="required">*</span></label>
              <input 
                type="text" 
                id="lastNameEng" 
                name="lastNameEng" 
                value={formData.lastNameEng} 
                onChange={handleChange} 
                required 
              />
            </div>
          </div>

          <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="dob">วัน-เดือน-ปี พ.ศ.เกิด (DD-MM-YYYY) <span className="required">*</span></label>
              <input 
                type="text"
                id="dob" 
                name="dob" 
                value={formData.dob} 
                onChange={handleChange}
                placeholder="DD-MM-YYYY (เช่น 01-01-2560)"
                required 
              />
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="idCardNumber">หมายเลขบัตรประชาชน <span className="required">*</span></label>
              <input
                type="text"
                id="idCardNumber"
                name="idCardNumber"
                value={formData.idCardNumber}
                onChange={handleChange}
                placeholder="กรอกตัวเลข 13 หลัก"
                required
              />
            </div>
          </div>



          <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 2 }}>
              <label htmlFor="currentSchool">โรงเรียนที่เรียนอยู่ปัจจุบัน <span className="required">*</span></label>
              <input
                type="text"
                id="currentSchool"
                name="currentSchool"
                value={formData.currentSchool}
                onChange={handleChange}
                placeholder="กรุณากรอกชื่อโรงเรียน"
                required
              />
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="grade">ระดับชั้น <span className="required">*</span></label>
              <select
                id="grade"
                name="grade"
                value={formData.grade}
                onChange={handleChange}
                required
              >
                <option value="">-- เลือกระดับชั้น --</option>
                <option value="อนุบาล1">อนุบาล 1</option>
                <option value="อนุบาล2">อนุบาล 2</option>
                <option value="อนุบาล3">อนุบาล 3</option>
                <option value="ประถมปีที่1">ประถมปีที่ 1</option>
                <option value="ประถมปีที่2">ประถมปีที่ 2</option>
                <option value="ประถมปีที่3">ประถมปีที่ 3</option>
                <option value="ประถมปีที่4">ประถมปีที่ 4</option>
                <option value="ประถมปีที่5">ประถมปีที่ 5</option>
                <option value="ประถมปีที่6">ประถมปีที่ 6</option>

              </select>
            </div>
          </div>

          <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="schoolProvince">จังหวัด (ของสถานศึกษา) <span className="required">*</span></label>
              <input
                type="text"
                id="schoolProvince"
                name="schoolProvince"
                value={formData.schoolProvince}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="studentProvince">จังหวัดที่ผู้สมัครพักอาศัย <span className="required">*</span></label>
              <input
                type="text"
                id="studentProvince"
                name="studentProvince"
                value={formData.studentProvince}
                onChange={handleChange}
                required
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="form-section-header">
            <h2>ข้อมูลผู้ปกครอง</h2>
          </div>

          <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="parentFirstName">ชื่อผู้ปกครอง <span className="required">*</span></label>
              <input 
                type="text" 
                id="parentFirstName" 
                name="parentFirstName" 
                value={formData.parentFirstName} 
                onChange={handleChange}
                style={{ width: '100%' }} 
                required
              />
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="parentLastName">นามสกุลผู้ปกครอง <span className="required">*</span></label>
              <input 
                type="text" 
                id="parentLastName" 
                name="parentLastName" 
                value={formData.parentLastName} 
                onChange={handleChange}
                style={{ width: '100%' }} 
                required
              />
            </div>
          </div>

          <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="contact1">เบอร์โทรติดต่อ <span className="required">*</span></label>
              <input 
                type="text" 
                id="contact1" 
                name="contact1" 
                value={formData.contact1} 
                onChange={handleChange}
                placeholder="XXX-XXXXXXX"
                pattern="\d{3}-\d{7}"
                title="เบอร์โทรศัพท์ต้องอยู่ในรูปแบบ XXX-XXXXXXX"
                style={{ width: '100%' }} 
                required
              />
            </div>

            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="lineId">Line ID (ของผู้ปกครอง)</label>
              <input 
                type="text" 
                id="lineId" 
                name="lineId" 
                value={formData.lineId} 
                onChange={handleChange}
                placeholder="กรอก Line ID"
                style={{ width: '100%' }} 
              />
            </div>
          </div>

          <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label htmlFor="email">อีเมล (Email)</label>
              <input 
                type="email" 
                id="email" 
                name="email" 
                value={formData.email} 
                onChange={handleChange}
                placeholder="example@email.com"
                style={{ width: '100%' }} 
              />
            </div>
          </div>

          <div className="form-row" style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group" style={{ flex: 1 }}>
              <label>ทราบข้อมูลโครงการ TMSC จากช่องทางไหน <span className="required">*</span></label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.5rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', fontWeight: 'normal' }}>
                  <input type="radio" name="tmscSource" value="จาก Facebook" checked={formData.tmscSource === 'จาก Facebook'} onChange={handleChange} required style={{ marginRight: '0.5rem', width: 'auto' }} />
                  1. จาก Facebook
                </label>
                <label style={{ display: 'flex', alignItems: 'center', fontWeight: 'normal' }}>
                  <input type="radio" name="tmscSource" value="จาก Website ของสถาบันพาวเวอร์วิสดอม" checked={formData.tmscSource === 'จาก Website ของสถาบันพาวเวอร์วิสดอม'} onChange={handleChange} style={{ marginRight: '0.5rem', width: 'auto' }} />
                  2. จาก Website ของสถาบันพาวเวอร์วิสดอม
                </label>
                <label style={{ display: 'flex', alignItems: 'center', fontWeight: 'normal' }}>
                  <input type="radio" name="tmscSource" value="จากทางโรงเรียนแนะนำ" checked={formData.tmscSource === 'จากทางโรงเรียนแนะนำ'} onChange={handleChange} style={{ marginRight: '0.5rem', width: 'auto' }} />
                  3. จากทางโรงเรียนแนะนำ
                </label>
                <label style={{ display: 'flex', alignItems: 'center', fontWeight: 'normal' }}>
                  <input type="radio" name="tmscSource" value="จากสถาบันสอนพิเศษ" checked={formData.tmscSource === 'จากสถาบันสอนพิเศษ'} onChange={handleChange} style={{ marginRight: '0.5rem', width: 'auto' }} />
                  4. จากสถาบันสอนพิเศษ
                </label>
                <label style={{ display: 'flex', alignItems: 'center', fontWeight: 'normal' }}>
                  <input type="radio" name="tmscSource" value="จากเพื่อนแนะนำ" checked={formData.tmscSource === 'จากเพื่อนแนะนำ'} onChange={handleChange} style={{ marginRight: '0.5rem', width: 'auto' }} />
                  5. จากเพื่อนแนะนำ
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" disabled={isSubmitting || isLoadingApplicationId} className="submit-button">
            {isSubmitting ? 'กำลังบันทึก...' : 'บันทึกข้อมูล'}
          </button>

          <button type="button" onClick={resetForm} className="reset-button" disabled={isSubmitting || isLoadingApplicationId}>
            เคลียร์ฟอร์ม
          </button>

          <button type="button" onClick={() => setShowSuccessImage(true)} className="info-button">
            ข้อมูลชำระเงิน
          </button>
        </div>
      </form>

      {showSuccessImage && (
        <div className="step-modal-overlay">
          <div className="step-modal-content">
            <picture>
              <source media="(min-width: 768px)" srcSet={process.env.PUBLIC_URL + '/information-landscript-1.png'} />
              <img 
                src={process.env.PUBLIC_URL + '/information-portail-1.png'} 
                alt="ข้อมูลเพิ่มเติม" 
                className="step-modal-image"
              />
            </picture>
            <button 
              onClick={() => setShowSuccessImage(false)}
              className="submit-button step-modal-btn"
            >
              ปิด
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentForm;